import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Pedido, OrderStatus, ItemPedido, Produto, Empresa, UserRole, Bairro, Categoria, AdicionalCategoria, AdicionalItem, UserProfile } from '../types';
import { supabaseService } from '../supabaseService';
import { usePedidos } from '../hooks/usePedidos';
import { formatCurrency } from '../constants';
import { useAuth } from '../hooks/useAuth';
import { 
  ResponsiveContainer, AreaChart, Area, CartesianGrid, XAxis, YAxis, Tooltip,
  PieChart, Pie, Cell, Legend
} from 'recharts';

// Novos Componentes Extraídos
import { AdminSidebar } from './admin/AdminSidebar';
import { AdminHeader } from './admin/AdminHeader';
import { AdminPedidos } from './admin/AdminPedidos';
import { AdminCategorias } from './admin/AdminCategorias';
import { AdminProdutos } from './admin/AdminProdutos';
import { AdminConfiguracoes } from './admin/AdminConfiguracoes';
import { AdminModais } from './admin/AdminModais';
import { AdminAdicionais } from './admin/AdminAdicionais';
import { AdminLogistica } from './admin/AdminLogistica';
import { AdminInsumos } from './admin/AdminInsumos';
import { AdminFichaTecnica } from './admin/AdminFichaTecnica';

const MOCK_BAIRROS_DEMO: Partial<Bairro>[] = [
  { id: 'b1', nome: 'Centro', taxa: 5 },
  { id: 'b2', nome: 'Vila Nova', taxa: 7 },
  { id: 'b3', nome: 'Jardim América', taxa: 10 },
  { id: 'b4', nome: 'Retirada na Loja', taxa: 0 }
];

const MOCK_PEDIDOS_DEMO: Partial<Pedido>[] = [
  { id: 'p1', nome_cliente: 'Cliente Novo', total: 45.9, status: OrderStatus.NOVO, created_at: new Date().toISOString(), endereco: 'Rua Principal, 100', whatsapp_cliente: '5511999999999' },
  { id: 'p2', nome_cliente: 'Cliente Aceito', total: 120.5, status: OrderStatus.ACEITO, created_at: new Date().toISOString(), endereco: 'Av. Central, 500', whatsapp_cliente: '5511888888888' },
  { id: 'p3', nome_cliente: 'Cliente em Preparo', total: 32.0, status: OrderStatus.PREPARO, created_at: new Date().toISOString(), endereco: 'Rua das Flores, 10', whatsapp_cliente: '5511777777777' },
  { id: 'p4', nome_cliente: 'Cliente em Entrega', total: 88.0, status: OrderStatus.ENTREGA, created_at: new Date().toISOString(), endereco: 'Alameda Norte, 45', whatsapp_cliente: '5511666666666' },
  { id: 'p5', nome_cliente: 'Cliente Concluído', total: 55.0, status: OrderStatus.CONCLUIDO, created_at: new Date().toISOString(), endereco: 'Rua Treze, 99', whatsapp_cliente: '5511555555555' }
];

export const AdminView: React.FC<{ isDemo?: boolean, user?: any }> = ({ isDemo = false, user: propUser }) => {
  const { user: authUser, profile: authProfile, loading, signOut } = useAuth();
  
  const user = propUser || authUser;
  const profile = (propUser?.role ? propUser : authProfile) as UserProfile | null;

  // Local state for company switching (Super Admin only or active profile)
  const [currentEmpresa, setCurrentEmpresa] = useState<Empresa | null>(profile?.empresas || null);
  const [empresas, setEmpresas] = useState<Empresa[]>([]);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [view, setView] = useState<'empresas' | 'pedidos' | 'produtos' | 'categorias' | 'configuracoes' | 'relatorios' | 'logistica' | 'complementos' | 'insumos' | 'ficha_tecnica'>(
    profile?.role === 'SUPER_ADMIN' ? 'empresas' : 'pedidos'
  );

  // Sync currentEmpresa when profile loads/remains
  useEffect(() => {
    if (profile?.empresas && !currentEmpresa) {
      setCurrentEmpresa(profile.empresas);
    }
  }, [profile, currentEmpresa]);

  const { pedidos: realPedidos, reload: reloadPedidos } = usePedidos(currentEmpresa?.id);
  
  const pedidos = useMemo(() => {
    if (!isDemo) return realPedidos;
    const all = [...realPedidos];
    MOCK_PEDIDOS_DEMO.forEach(p => {
        if (!all.find(x => x.status === p.status)) all.push(p as Pedido);
    });
    return all.sort((a,b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
  }, [realPedidos, isDemo]);

  const [produtos, setProdutos] = useState<Produto[]>([]);
  const [bairros, setBairros] = useState<Bairro[]>([]);
  const [categorias, setCategorias] = useState<Categoria[]>([]);
  const [adicionaisCats, setAdicionaisCats] = useState<AdicionalCategoria[]>([]);
  
  const [selectedPedido, setSelectedPedido] = useState<Pedido | null>(null);
  const [itens, setItens] = useState<ItemPedido[]>([]);
  const [activeTab, setActiveTab] = useState<'entrada' | 'producao' | 'historico'>('entrada');
  
  const [isEditing, setIsEditing] = useState(false);
  const [currentProduto, setCurrentProduto] = useState<Partial<Produto> | null>(null);
  const [isEditingCategoria, setIsEditingCategoria] = useState(false);
  const [currentCategoria, setCurrentCategoria] = useState<Partial<Categoria> | null>(null);
  const [isEditingAdicionalCat, setIsEditingAdicionalCat] = useState(false);
  const [currentAdicionalCat, setCurrentAdicionalCat] = useState<Partial<AdicionalCategoria> | null>(null);
  const [isEditingAdicionalItem, setIsEditingAdicionalItem] = useState(false);
  const [currentAdicionalItem, setCurrentAdicionalItem] = useState<Partial<AdicionalItem> | null>(null);
  const [isEditingBairro, setIsEditingBairro] = useState(false);
  const [currentBairro, setCurrentBairro] = useState<Partial<Bairro> | null>(null);
  const [isCreatingEmpresa, setIsCreatingEmpresa] = useState(false);
  const [newEmpresaData, setNewEmpresaData] = useState({ nome: '', slug: '', email: '', senha: '' });
  const [selectedFichaProdutoId, setSelectedFichaProdutoId] = useState<string | null>(null);

  const [isUpdating, setIsUpdating] = useState(false);
  const [novaSenha, setNovaSenha] = useState('');

  const demoAlert = () => alert("Este é um ambiente de demonstração.\nPara editar e receber pedidos reais, contrate um plano.");

  const loadEmpresas = useCallback(async () => {
    if (profile?.role === UserRole.SUPER_ADMIN) {
      const data = await supabaseService.getEmpresas();
      setEmpresas(data);
    }
  }, [profile]);

  useEffect(() => { loadEmpresas(); }, [loadEmpresas]);

  const loadAdminData = useCallback(async () => {
    if (!currentEmpresa?.id) return;
    try {
      const [prods, bairs, cats, adCats] = await Promise.all([
        supabaseService.getProdutos(currentEmpresa.id),
        supabaseService.getBairros(currentEmpresa.id),
        supabaseService.getCategorias(currentEmpresa.id),
        supabaseService.getAdicionaisCategorias(currentEmpresa.id)
      ]);
      setProdutos(prods);
      setBairros(isDemo ? [...bairs, ...MOCK_BAIRROS_DEMO as Bairro[]] : bairs);
      setCategorias(cats);
      setAdicionaisCats(adCats);
    } catch (err) { console.error(err); }
  }, [currentEmpresa?.id, isDemo]);

  useEffect(() => { loadAdminData(); }, [loadAdminData]);

  const dashboardStats = useMemo(() => {
    const concluido = pedidos.filter(p => p.status === OrderStatus.CONCLUIDO);
    const totalVendas = concluido.reduce((acc, p) => acc + p.total, 0);
    const count = concluido.length;
    const ticket = count > 0 ? totalVendas / count : 0;
    
    const last7Days = Array.from({length: 7}, (_, i) => {
      const d = new Date(); d.setDate(d.getDate() - (6 - i));
      return d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
    });
    
    const salesByDay = last7Days.map(day => {
      const daySales = concluido.filter(p => new Date(p.created_at).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' }) === day).reduce((acc, p) => acc + p.total, 0);
      return { name: day, total: daySales };
    });

    const categoryDistribution = categorias.map(cat => ({ name: cat.nome, value: produtos.filter(p => p.categoria === cat.nome).length })).filter(c => c.value > 0).slice(0, 5);
    if (categoryDistribution.length === 0) categoryDistribution.push({ name: 'Sem dados', value: 1 });
    return { totalVendas, count, ticket, salesByDay, categoryDistribution, COLORS: ['#ea580c', '#0f172a', '#3b82f6', '#8b5cf6', '#10b981'] };
  }, [pedidos, categorias, produtos]);

  const openPedido = async (p: Pedido) => {
    setSelectedPedido(p);
    if (p.id.startsWith('p')) {
        setItens([{ id: 'i1', pedido_id: p.id, produto_id: '1', quantidade: 1, preco_unitario: p.total, produto_nome: 'Item Demonstrativo', observacao: 'Pedido fictício para visualização.' }]);
    } else {
        const i = await supabaseService.getItensPedido(p.id);
        setItens(i);
    }
  };

  const updateStatus = async (pId: string, status: OrderStatus) => {
    if (isDemo) return demoAlert();
    if (isUpdating) return;
    setIsUpdating(true);
    try {
      await supabaseService.atualizarStatusPedido(pId, status);
      await reloadPedidos();
    } catch (err: any) { alert(err.message); }
    finally { setIsUpdating(false); }
  };

  const handleSaveConfigGeral = async () => {
    if (isDemo) return demoAlert();
    if (!currentEmpresa) return;
    setIsUpdating(true);
    try {
      await supabaseService.atualizarEmpresa(currentEmpresa.id, currentEmpresa);
      if (novaSenha.trim() !== '') {
        // Updated to use auth.users via authService logic
        await supabaseService.updateUser(user?.id || '', { senha: novaSenha });
        setNovaSenha('');
      }
      alert("Configurações salvas!");
    } catch (err: any) { alert(err.message); }
    finally { setIsUpdating(false); }
  };

  const handleUpdateHorario = (dia: string, campo: 'abertura' | 'fechamento', valor: string) => {
    if (isDemo) return;
    if (!currentEmpresa) return;
    let h = {}; try { h = JSON.parse(currentEmpresa.horarios || '{}'); } catch {}
    const nh = { ...h, [dia]: { ...((h as any)[dia] || {}), [campo]: valor } };
    setCurrentEmpresa({ ...currentEmpresa, horarios: JSON.stringify(nh) });
  };

  const handleSaveProduto = async (e: React.FormEvent) => {
    e.preventDefault(); if (isDemo) return demoAlert();
    if (!currentProduto || !currentEmpresa) return;
    setIsUpdating(true);
    try { await supabaseService.salvarProduto({ ...currentProduto, empresa_id: currentEmpresa.id }); setIsEditing(false); loadAdminData(); }
    catch (err: any) { alert(err.message); } finally { setIsUpdating(false); }
  };

  const handleDeleteProduto = async (id: string) => {
    if (isDemo) return demoAlert(); setIsUpdating(true);
    try { await supabaseService.deletarProduto(id); loadAdminData(); } catch (err: any) { alert(err.message); }
    finally { setIsUpdating(false); }
  };

  const handleSaveCategoria = async (e: React.FormEvent) => {
    e.preventDefault(); if (isDemo) return demoAlert();
    if (!currentCategoria || !currentEmpresa) return;
    setIsUpdating(true);
    try { await supabaseService.salvarCategoria({ ...currentCategoria, empresa_id: currentEmpresa.id }); setIsEditingCategoria(false); loadAdminData(); }
    catch (err: any) { alert(err.message); } finally { setIsUpdating(false); }
  };

  const handleDeleteCategoria = async (id: string) => {
    if (isDemo) return demoAlert(); setIsUpdating(true);
    try { await supabaseService.deletarCategoria(id); loadAdminData(); } catch (err: any) { alert(err.message); }
    finally { setIsUpdating(false); }
  };

  const handleMoverCategoria = async (cat: Categoria, direcao: 'sobe' | 'desce') => {
    if (isDemo) return demoAlert();
    const idx = categorias.findIndex(c => c.id === cat.id);
    if (direcao === 'sobe' && idx === 0) return;
    if (direcao === 'desce' && idx === categorias.length - 1) return;
    const vizinho = direcao === 'sobe' ? categorias[idx - 1] : categorias[idx + 1];
    setIsUpdating(true);
    try { await Promise.all([supabaseService.salvarCategoria({ ...cat, ordem: vizinho.ordem }), supabaseService.salvarCategoria({ ...vizinho, ordem: cat.ordem })]); await loadAdminData(); }
    catch (err: any) { alert(err.message); } finally { setIsUpdating(false); }
  };

  const handleSaveAdicionalCat = async (e: React.FormEvent) => {
    e.preventDefault(); if (isDemo) return demoAlert();
    if (!currentAdicionalCat || !currentEmpresa) return;
    setIsUpdating(true);
    try { await supabaseService.salvarAdicionalCategoria({ ...currentAdicionalCat, empresa_id: currentEmpresa.id }); setIsEditingAdicionalCat(false); loadAdminData(); }
    catch (err: any) { alert(err.message); } finally { setIsUpdating(false); }
  };

  const handleDeleteAdicionalCat = async (id: string) => {
    if (isDemo) return demoAlert(); setIsUpdating(true);
    try { await supabaseService.deletarAdicionalCategoria(id); loadAdminData(); } catch (err: any) { alert(err.message); }
    finally { setIsUpdating(false); }
  };

  const handleSaveAdicionalItem = async (e: React.FormEvent) => {
    e.preventDefault(); if (isDemo) return demoAlert();
    if (!currentAdicionalItem) return; setIsUpdating(true);
    try { await supabaseService.salvarAdicionalItem(currentAdicionalItem); setIsEditingAdicionalItem(false); loadAdminData(); }
    catch (err: any) { alert(err.message); } finally { setIsUpdating(false); }
  };

  const handleDeleteAdicionalItem = async (id: string) => {
    if (isDemo) return demoAlert(); setIsUpdating(true);
    try { await supabaseService.deletarAdicionalItem(id); loadAdminData(); } catch (err: any) { alert(err.message); }
    finally { setIsUpdating(false); }
  };

  const handleSaveBairro = async (e: React.FormEvent) => {
    e.preventDefault(); if (isDemo) return demoAlert();
    if (!currentBairro || !currentEmpresa) return; setIsUpdating(true);
    try { await supabaseService.salvarBairro({ ...currentBairro, empresa_id: currentEmpresa.id }); setIsEditingBairro(false); loadAdminData(); }
    catch (err: any) { alert(err.message); } finally { setIsUpdating(false); }
  };

  const handleDeleteBairro = async (id: string) => {
    if (isDemo) return demoAlert(); setIsUpdating(true);
    try { await supabaseService.deletarBairro(id); loadAdminData(); } catch (err: any) { alert(err.message); }
    finally { setIsUpdating(false); }
  };

  const handleCreateEmpresa = async (e: React.FormEvent) => {
    e.preventDefault(); if (isDemo) return demoAlert();
    setIsUpdating(true);
    try { await supabaseService.criarEmpresa({ nome: newEmpresaData.nome, slug: newEmpresaData.slug }, newEmpresaData.email, newEmpresaData.senha); alert("Empresa criada!"); setIsCreatingEmpresa(false); setNewEmpresaData({ nome: '', slug: '', email: '', senha: '' }); loadEmpresas(); }
    catch (err: any) { alert(err.message); } finally { setIsUpdating(false); }
  };

  const horariosParsed = useMemo(() => { try { return JSON.parse(currentEmpresa?.horarios || '{}'); } catch { return {}; } }, [currentEmpresa?.horarios]);

  if (loading) {
    return (
      <div className="h-screen flex items-center justify-center bg-slate-50">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-4 border-orange-600 border-t-transparent rounded-full animate-spin"></div>
          <p className="text-slate-600 font-bold">Carregando painel...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen bg-slate-50 overflow-hidden font-sans relative">
      <AdminSidebar user={{ ...user, role: profile?.role }} view={view} setView={(v) => { setView(v); setSidebarOpen(false); }} currentEmpresa={currentEmpresa} onSignOut={() => signOut()} isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />
      <main className="flex-1 flex flex-col min-w-0 overflow-hidden">
        <AdminHeader user={{ ...user, role: profile?.role }} currentEmpresa={currentEmpresa} view={view} isDemo={isDemo} onOpenSidebar={() => setSidebarOpen(true)} />
        <div className="flex-1 overflow-y-auto custom-scrollbar">
          {view === 'pedidos' && <AdminPedidos pedidos={pedidos} selectedPedido={selectedPedido} setSelectedPedido={setSelectedPedido} itens={itens} openPedido={openPedido} updateStatus={updateStatus} activeTab={activeTab} setActiveTab={setActiveTab} isUpdating={isUpdating} isDemo={isDemo} />}
          {view === 'categorias' && <AdminCategorias categorias={categorias} isUpdating={isUpdating} handleMoverCategoria={handleMoverCategoria} setIsEditingCategoria={setIsEditingCategoria} setCurrentCategoria={setCurrentCategoria} handleDeleteCategoria={handleDeleteCategoria} isDemo={isDemo} />}
          {view === 'produtos' && <AdminProdutos produtos={produtos} categorias={categorias} setIsEditing={setIsEditing} setCurrentProduto={setCurrentProduto} handleDeleteProduto={handleDeleteProduto} onOpenFicha={(id) => { setSelectedFichaProdutoId(id); setView('ficha_tecnica'); }} isDemo={isDemo} />}
          {view === 'complementos' && <AdminAdicionais adicionaisCats={adicionaisCats} isUpdating={isUpdating} setIsEditingAdicionalCat={setIsEditingAdicionalCat} setCurrentAdicionalCat={setCurrentAdicionalCat} setIsEditingAdicionalItem={setIsEditingAdicionalItem} setCurrentAdicionalItem={setCurrentAdicionalItem} handleDeleteAdicionalCat={handleDeleteAdicionalCat} handleDeleteAdicionalItem={handleDeleteAdicionalItem} isDemo={isDemo} />}
          {view === 'logistica' && <AdminLogistica bairros={bairros} isUpdating={isUpdating} setIsEditingBairro={setIsEditingBairro} setCurrentBairro={setCurrentBairro} handleDeleteBairro={handleDeleteBairro} isDemo={isDemo} />}
          {view === 'insumos' && currentEmpresa && <AdminInsumos empresaId={currentEmpresa.id} isDemo={isDemo} />}
          {view === 'ficha_tecnica' && currentEmpresa && <AdminFichaTecnica empresaId={currentEmpresa.id} produtos={produtos} selectedProdutoId={selectedFichaProdutoId} setSelectedProdutoId={setSelectedFichaProdutoId} isDemo={isDemo} />}
          {view === 'configuracoes' && <AdminConfiguracoes currentEmpresa={currentEmpresa} setCurrentEmpresa={setCurrentEmpresa} handleSaveConfigGeral={handleSaveConfigGeral} handleUpdateHorario={handleUpdateHorario} horariosParsed={horariosParsed} isUpdating={isUpdating} novaSenha={novaSenha} setNovaSenha={setNovaSenha} isDemo={isDemo} />}
          {view === 'relatorios' && (
            <div className="p-4 md:p-10 animate-fade-in space-y-6 max-w-7xl mx-auto">
               <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 md:gap-8">
                  <div className="bg-white p-6 md:p-10 rounded-3xl border border-slate-200">
                    <p className="text-[10px] md:text-[11px] font-black text-slate-400 uppercase tracking-widest mb-3">Vendas Totais</p>
                    <p className="text-3xl md:text-5xl font-black text-slate-900 tracking-tighter">{formatCurrency(dashboardStats.totalVendas)}</p>
                  </div>
                  <div className="bg-white p-6 md:p-10 rounded-3xl border border-slate-200">
                    <p className="text-[10px] md:text-[11px] font-black text-slate-400 uppercase tracking-widest mb-3">Pedidos</p>
                    <p className="text-3xl md:text-5xl font-black text-slate-900 tracking-tighter">{dashboardStats.count}</p>
                  </div>
                  <div className="bg-white p-6 md:p-10 rounded-3xl border border-slate-200">
                    <p className="text-[10px] md:text-[11px] font-black text-slate-400 uppercase tracking-widest mb-3">Ticket Médio</p>
                    <p className="text-3xl md:text-5xl font-black text-slate-900 tracking-tighter">{formatCurrency(dashboardStats.ticket)}</p>
                  </div>
               </div>
               <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 md:gap-8">
                  <div className="lg:col-span-2 bg-white p-6 md:p-12 rounded-3xl border border-slate-200">
                    <h3 className="text-lg md:text-xl font-black mb-6 md:mb-10 tracking-tight">Faturamento Diário</h3>
                    <div className="h-64 md:h-80 w-full"><ResponsiveContainer width="100%" height="100%"><AreaChart data={dashboardStats.salesByDay}><defs><linearGradient id="colorTotal" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#ea580c" stopOpacity={0.1}/><stop offset="95%" stopColor="#ea580c" stopOpacity={0}/></linearGradient></defs><CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" /><XAxis dataKey="name" axisLine={false} tickLine={false} tick={{fontSize: 9, fontWeight: 700, fill: '#94a3b8'}} /><YAxis axisLine={false} tickLine={false} tick={{fontSize: 9, fontWeight: 700, fill: '#94a3b8'}} /><Tooltip contentStyle={{borderRadius: '16px', border: 'none', boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1)', fontWeight: 800}} /><Area type="monotone" dataKey="total" stroke="#ea580c" strokeWidth={4} fillOpacity={1} fill="url(#colorTotal)" /></AreaChart></ResponsiveContainer></div>
                  </div>
                  <div className="bg-white p-6 md:p-12 rounded-3xl border border-slate-200 flex flex-col">
                    <h3 className="text-lg md:text-xl font-black mb-6 md:mb-10 tracking-tight">Mix de Categorias</h3>
                    <div className="h-64 md:h-80 w-full flex-1"><ResponsiveContainer width="100%" height="100%"><PieChart><Pie data={dashboardStats.categoryDistribution} cx="50%" cy="40%" innerRadius={50} outerRadius={70} paddingAngle={5} dataKey="value">{dashboardStats.categoryDistribution.map((_, i) => <Cell key={`cell-${i}`} fill={dashboardStats.COLORS[i % dashboardStats.COLORS.length]} />)}</Pie><Tooltip contentStyle={{borderRadius: '16px', border: 'none', boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1)', fontWeight: 800}} /><Legend verticalAlign="bottom" align="center" iconType="circle" formatter={(v) => <span className="text-[9px] font-black text-slate-500 uppercase tracking-widest ml-1">{v}</span>} /></PieChart></ResponsiveContainer></div>
                  </div>
               </div>
            </div>
          )}
          {view === 'empresas' && profile?.role === UserRole.SUPER_ADMIN && (
            <div className="p-4 md:p-10 animate-fade-in space-y-6 md:space-y-10">
               <div className="flex flex-col md:flex-row justify-between items-start md:items-center bg-slate-900 p-6 md:p-8 rounded-3xl shadow-2xl gap-4">
                  <div>
                    <h3 className="text-xl md:text-2xl font-black text-white tracking-tight">Inquilinos Delyon</h3>
                    <p className="text-slate-400 font-bold text-xs md:text-sm mt-1">Gerencie todos os restaurantes ativos.</p>
                  </div>
                  <button onClick={() => setIsCreatingEmpresa(true)} className="w-full md:w-auto bg-orange-600 text-white px-6 py-4 rounded-2xl font-black shadow-xl hover:bg-orange-700 transition-all flex items-center justify-center gap-3"><svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M12 4v16m8-8H4"></path></svg>Novo Restaurante</button>
               </div>
               <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
                 {empresas.map(emp => (
                   <div key={emp.id} className="bg-white border border-slate-200 p-6 rounded-[2rem] shadow-sm hover:shadow-md transition-all">
                      <div className="flex items-center gap-4 mb-6"><div className="w-14 h-14 bg-slate-50 rounded-2xl border border-slate-100 overflow-hidden shrink-0 flex items-center justify-center font-black text-slate-300">{emp.logo_url ? <img src={emp.logo_url} className="w-full h-full object-cover" /> : emp.nome.charAt(0)}</div><div className="min-w-0 flex-1"><h4 className="font-black text-slate-900 truncate">{emp.nome}</h4><p className="text-[10px] font-bold text-slate-400">/{emp.slug}</p></div></div>
                      <div className="flex gap-2 mt-6"><button onClick={() => { setCurrentEmpresa(emp); setView('pedidos'); }} className="flex-1 py-3 rounded-xl bg-slate-900 text-white text-[10px] font-black uppercase tracking-widest hover:bg-orange-600 transition-all">Entrar Painel</button></div>
                   </div>
                 ))}
               </div>
            </div>
          )}
        </div>
      </main>
      <AdminModais isEditing={isEditing} setIsEditing={setIsEditing} currentProduto={currentProduto} setCurrentProduto={setCurrentProduto} isEditingCategoria={isEditingCategoria} setIsEditingCategoria={setIsEditingCategoria} currentCategoria={currentCategoria} setCurrentCategoria={setCurrentCategoria} isEditingAdicionalCat={isEditingAdicionalCat} setIsEditingAdicionalCat={setIsEditingAdicionalCat} currentAdicionalCat={currentAdicionalCat} setCurrentAdicionalCat={setCurrentAdicionalCat} isEditingAdicionalItem={isEditingAdicionalItem} setIsEditingAdicionalItem={setIsEditingAdicionalItem} currentAdicionalItem={currentAdicionalItem} setCurrentAdicionalItem={setCurrentAdicionalItem} isEditingBairro={isEditingBairro} setIsEditingBairro={setIsEditingBairro} currentBairro={currentBairro} setCurrentBairro={setCurrentBairro} isCreatingEmpresa={isCreatingEmpresa} setIsCreatingEmpresa={setIsCreatingEmpresa} newEmpresaData={newEmpresaData} setNewEmpresaData={setNewEmpresaData} handleSaveProduto={handleSaveProduto} handleSaveCategoria={handleSaveCategoria} handleSaveAdicionalCat={handleSaveAdicionalCat} handleSaveAdicionalItem={handleSaveAdicionalItem} handleSaveBairro={handleSaveBairro} handleCreateEmpresa={handleCreateEmpresa} isUpdating={isUpdating} categorias={categorias} adicionaisCats={adicionaisCats} isDemo={isDemo} />
      <style>{`.custom-scrollbar::-webkit-scrollbar { width: 4px; } .custom-scrollbar::-webkit-scrollbar-track { background: transparent; } .custom-scrollbar::-webkit-scrollbar-thumb { background: #e2e8f0; border-radius: 10px; }`}</style>
    </div>
  );
};